/**
 * PreviewCountdownPill.tsx
 *
 * Small persistent pill shown during the 48-hour Premium Preview.
 * Renders only when previewActive === true and a valid time remains.
 * Updates every 60 seconds. Tapping navigates to /paywall.
 *
 * Usage — drop into any screen header in place of the right-side spacer:
 *
 *   <div className="flex items-center justify-between">
 *     <button ...>back</button>
 *     <h1>Screen Title</h1>
 *     <PreviewCountdownPill />
 *   </div>
 *
 * When preview is inactive the component returns null and the layout
 * is unaffected — the spacer div is no longer needed.
 * If no preview is active, reinstate <div className="w-12" /> in its place
 * to maintain header balance. The pill's min-width: 48px matches w-12.
 *
 * Target: src/components/PreviewCountdownPill.tsx
 */

import { useAppContext } from '../store/AppContext';
import { useDemoNavigate } from '../hooks/useDemoNavigate';
import { usePreviewCountdown } from '../hooks/usePreviewCountdown';

export function PreviewCountdownPill() {
  const { previewActive, premiumPreviewExpiresAt, isDemoMode, refetchSubscription } = useAppContext();
  const navigate = useDemoNavigate();
  const remaining = usePreviewCountdown(previewActive, premiumPreviewExpiresAt, refetchSubscription);

  if (!previewActive || !remaining) return <div className="w-12" />;

  const label = remaining.hours > 0
    ? `${remaining.hours}h ${remaining.minutes}m preview`
    : `${remaining.minutes}m preview`;

  return (
    <button
      type="button"
      onClick={() => !isDemoMode && navigate('/paywall')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: '#E1F5EE',
        border: '0.5px solid #9FE1CB',
        borderRadius: 20,
        padding: '3px 8px',
        cursor: isDemoMode ? 'default' : 'pointer',
        flexShrink: 0,
        minWidth: 48,
      }}
      aria-label={`Premium Preview active — ${label}`}
    >
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#0F6E56', flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 500, color: '#085041', lineHeight: 1, whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
}
